import React from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  usePolicy,
  useAddRule,
  useRemoveRule,
  useActivatePolicy,
  useArchivePolicy,
  useTestDetection,
  useNerCategories,
} from '@redbax/shared';
import type { DetectionTestResponse, DetectorType, FileClassificationLevel } from '@redbax/shared';
import {
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DataTable,
  LoadingSkeleton,
  Separator,
  type Column,
} from '@redbax/ui-kit';
import { ArrowLeft, Plus, Trash2, Play } from 'lucide-react';
import type { DetectionRule } from '@redbax/shared';

const ruleColumns = (onRemove: (id: string) => void): Column<DetectionRule>[] => [
  { key: 'name', header: 'Name', sortable: true },
  {
    key: 'detectorType',
    header: 'Detector',
    render: (row) => (
      <Badge variant={row.detectorType === 'Ner' ? 'default' : row.detectorType === 'FileClassification' ? 'warning' : 'outline'}>
        {row.detectorType === 'Ner' ? 'NER' : row.detectorType === 'FileClassification' ? 'File Label' : 'Regex'}
      </Badge>
    ),
  },
  { key: 'category', header: 'Category', sortable: true },
  {
    key: 'pattern',
    header: 'Pattern / Category',
    render: (row) => (
      <span className="text-sm">
        {row.detectorType === 'Ner' ? row.nerCategory ?? '-' : row.detectorType === 'FileClassification' ? row.fileClassificationLevel ?? '-' : row.pattern}
      </span>
    ),
  },
  {
    key: 'action',
    header: 'Action',
    render: (row) => (
      <span className="flex items-center gap-1.5">
        <Badge variant={row.action === 'Block' ? 'destructive' : row.action === 'Warn' ? 'warning' : 'secondary'}>
          {row.action}
        </Badge>
        {row.action === 'Warn' && row.redact && (
          <Badge variant="outline">Redact</Badge>
        )}
      </span>
    ),
  },
  { key: 'confidenceThreshold', header: 'Threshold' },
  {
    key: 'caseSensitive',
    header: 'Case Sensitive',
    render: (row) => (
      row.detectorType === 'Regex' ? (
        <Badge variant={row.caseSensitive ? 'default' : 'secondary'}>
          {row.caseSensitive ? 'Yes' : 'No'}
        </Badge>
      ) : (
        <span className="text-xs text-text-secondary">N/A</span>
      )
    ),
  },
  {
    key: 'id',
    header: '',
    render: (row) => (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(row.id);
        }}
        className="text-text-secondary hover:text-red-accent"
        aria-label="Remove rule"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    ),
  },
];

export function PolicyEditorPage() {
  const { policyId } = useParams<{ policyId: string }>();
  const navigate = useNavigate();
  const { data: policy, isLoading } = usePolicy(policyId ?? '');
  const addRule = useAddRule(policyId ?? '');
  const removeRule = useRemoveRule(policyId ?? '');
  const activatePolicy = useActivatePolicy();
  const archivePolicy = useArchivePolicy();
  const testDetection = useTestDetection();
  const { data: nerCategories } = useNerCategories();

  const [detectorType, setDetectorType] = React.useState<DetectorType>('Regex');
  const [ruleName, setRuleName] = React.useState('');
  const [category, setCategory] = React.useState('PII');
  const [pattern, setPattern] = React.useState('');
  const [nerCategory, setNerCategory] = React.useState('');
  const [action, setAction] = React.useState('Block');
  const [threshold, setThreshold] = React.useState('0.8');
  const [caseSensitive, setCaseSensitive] = React.useState(false);
  const [fileClassificationLevel, setFileClassificationLevel] = React.useState<FileClassificationLevel>('Confidential');
  const [redact, setRedact] = React.useState(false);

  const [testContent, setTestContent] = React.useState('');
  const [testResult, setTestResult] = React.useState<DetectionTestResponse | null>(null);

  const isNer = detectorType === 'Ner';
  const isFileClassification = detectorType === 'FileClassification';
  const canAddRule = ruleName.trim() && (isFileClassification ? true : isNer ? !!nerCategory : !!pattern.trim());

  const handleAddRule = () => {
    if (!canAddRule) return;
    addRule.mutate({
      category: isFileClassification ? 'Confidential' : category,
      name: ruleName,
      pattern: isFileClassification ? '' : isNer ? '' : pattern,
      action,
      confidenceThreshold: isFileClassification ? 1.0 : parseFloat(threshold),
      caseSensitive: isNer || isFileClassification ? false : caseSensitive,
      detectorType,
      nerCategory: isNer ? nerCategory : undefined,
      fileClassificationLevel: isFileClassification ? fileClassificationLevel : undefined,
      redact: action === 'Warn' ? redact : undefined,
    });
    setRuleName('');
    setPattern('');
    setNerCategory('');
    setCaseSensitive(false);
    setRedact(false);
  };

  const handleTest = () => {
    if (!testContent.trim()) return;
    testDetection.mutate(
      { content: testContent, targetApplication: 'test-console' },
      { onSuccess: (data) => setTestResult(data) },
    );
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Policy" />
        <LoadingSkeleton rows={6} />
      </div>
    );
  }

  if (!policy) {
    return (
      <div>
        <PageHeader title="Policy not found" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={policy.name}
        description={policy.description ?? undefined}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/policies')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            {policy.status === 'Draft' && (
              <Button onClick={() => activatePolicy.mutate(policy.id)}>Activate</Button>
            )}
            {policy.status === 'Active' && (
              <Button variant="secondary" onClick={() => archivePolicy.mutate(policy.id)}>
                Archive
              </Button>
            )}
          </div>
        }
      />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Detection Rules</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={ruleColumns((id) => removeRule.mutate(id))}
              data={policy.rules}
              keyExtractor={(r) => r.id}
              emptyMessage="No rules added yet"
            />

            <Separator className="my-4" />

            <div className="mb-3 flex gap-2">
              <button
                type="button"
                onClick={() => setDetectorType('Regex')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  detectorType === 'Regex'
                    ? 'bg-red-accent text-white'
                    : 'bg-surface-raised text-text-secondary hover:text-text-primary'
                }`}
              >
                Regex
              </button>
              <button
                type="button"
                onClick={() => setDetectorType('Ner')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  detectorType === 'Ner'
                    ? 'bg-red-accent text-white'
                    : 'bg-surface-raised text-text-secondary hover:text-text-primary'
                }`}
              >
                NER (AI Model)
              </button>
              <button
                type="button"
                onClick={() => setDetectorType('FileClassification')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  detectorType === 'FileClassification'
                    ? 'bg-red-accent text-white'
                    : 'bg-surface-raised text-text-secondary hover:text-text-primary'
                }`}
              >
                File Classification
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
              <div>
                <Label>Name</Label>
                <Input value={ruleName} onChange={(e) => setRuleName(e.target.value)} placeholder="Rule name" className="mt-1" />
              </div>
              {!isFileClassification && (
                <div>
                  <Label>Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PII">PII</SelectItem>
                      <SelectItem value="PCI">PCI</SelectItem>
                      <SelectItem value="Confidential">Confidential</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isFileClassification ? (
                <div>
                  <Label>Minimum Level</Label>
                  <Select value={fileClassificationLevel} onValueChange={(v) => setFileClassificationLevel(v as FileClassificationLevel)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Public">Public</SelectItem>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="Confidential">Confidential</SelectItem>
                      <SelectItem value="HighlyConfidential">Highly Confidential</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : isNer ? (
                <div>
                  <Label>NER Category</Label>
                  <Select value={nerCategory} onValueChange={setNerCategory}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select entity type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(nerCategories ?? []).map((nc) => (
                        <SelectItem key={nc.value} value={nc.value}>
                          {nc.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>Pattern</Label>
                  <Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="Regex" className="mt-1" />
                </div>
              )}
              <div>
                <Label>Action</Label>
                <Select value={action} onValueChange={(v) => { setAction(v); if (v !== 'Warn') setRedact(false); }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Block">Block</SelectItem>
                    <SelectItem value="Warn">Warn</SelectItem>
                    <SelectItem value="Log">Log</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!isNer && !isFileClassification && (
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-text-primary">
                    <input
                      type="checkbox"
                      checked={caseSensitive}
                      onChange={(e) => setCaseSensitive(e.target.checked)}
                      className="rounded border-border"
                    />
                    Case Sensitive
                  </label>
                </div>
              )}
              {action === 'Warn' && (
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-text-primary">
                    <input
                      type="checkbox"
                      checked={redact}
                      onChange={(e) => setRedact(e.target.checked)}
                      className="rounded border-border"
                    />
                    Redact content
                  </label>
                </div>
              )}
              <div className="flex items-end">
                <Button onClick={handleAddRule} disabled={!canAddRule}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Detection Tester</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <textarea
                className="w-full rounded-md border border-border bg-surface p-3 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-red-accent"
                rows={4}
                value={testContent}
                onChange={(e) => setTestContent(e.target.value)}
                placeholder="Paste content to test detection rules..."
              />
              <Button onClick={handleTest} disabled={!testContent.trim() || testDetection.isPending}>
                <Play className="mr-1 h-4 w-4" />
                {testDetection.isPending ? 'Testing...' : 'Test'}
              </Button>
              {testResult && (
                <div className="mt-3 rounded-md border border-border bg-surface-raised p-4">
                  <p className="text-sm font-medium text-text-primary">
                    {testResult.detections.length} detection{testResult.detections.length !== 1 ? 's' : ''} found
                    <span className="ml-2 text-xs text-text-secondary">
                      ({testResult.processingTimeMs.toFixed(1)}ms)
                    </span>
                  </p>
                  {testResult.detections.map((d, i) => (
                    <div key={i} className="mt-2 flex items-center gap-2">
                      <Badge variant={d.action === 'Block' ? 'destructive' : d.action === 'Warn' ? 'warning' : 'secondary'}>
                        {d.action}
                      </Badge>
                      <span className="text-sm text-text-primary">{d.ruleName}</span>
                      <span className="text-xs text-text-secondary">
                        ({d.category}, {(d.confidence * 100).toFixed(0)}%)
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
